import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React, { ReactNode, useEffect, useState } from 'react';
import Button from '../Button/Button';

type TreeNodeProps = {
  item: TreeViewItem;
  path: number[];
  labelRenderer?: ItemRenderer;
  expandedIds?: Set<string>;
  onExpandItem?: OnExpandItem;
};

const TreeNode: React.FC<TreeNodeProps> = (props) => {
  const { item, path, labelRenderer, expandedIds, onExpandItem } = props;
  const { id, label, selected, defaultOpen, children } = item;

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const nodeId = id ?? `tree-${path.join('-')}`;
  const headingId = `${nodeId}-heading`;
  const collapseId = `${nodeId}-collapse`;

  const [expanded, setExpanded] = useState(
    expandedIds?.has(nodeId) ?? defaultOpen ?? false
  );

  useEffect(() => {
    if (typeof expandedIds === 'undefined') {
      return;
    }

    if (!id) {
      console.error(
        "TreeView items must have a 'id' property, if you want to use expandedIds."
      );
      return;
    }

    if (expandedIds.has(id) && hasChildren) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'id'
  }, [expandedIds, hasChildren]);

  const handleExpand = (newExpandedState: boolean) => () => {
    // Expanded state is controlled internally if expandedIds is not provided
    if (!expandedIds) {
      setExpanded(newExpandedState);
      return;
    }

    if (!id) {
      console.error(
        "TreeView items must have a 'id' property, if you want to use onExpandItem."
      );
      return;
    }

    if (!onExpandItem) {
      console.error(
        "TreeView items must have an 'onExpandItem' callback, if you want to use expandedIds."
      );
      return;
    }

    onExpandItem(item as TreeViewItemWithId, newExpandedState);
  };

  return (
    <>
      <div
        className="space-y-2"
        role="treeitem"
        aria-expanded={expanded}
        id={headingId}
      >
        <div className="flex w-full items-center">
          <div>
            <Button
              size="xs"
              variant="quaternary"
              aria-controls={collapseId}
              disabled={!hasChildren}
              onClick={hasChildren ? handleExpand(!expanded) : undefined}
            >
              <UntitledIcon
                className="w-4 h-4"
                name={expanded ? 'chevron-down' : 'chevron-right'}
              />
            </Button>
          </div>

          {labelRenderer ? (
            labelRenderer(item, expanded)
          ) : (
            <div
              className={clstx(
                'grow px-2 rounded-md',
                selected && 'bg-gray-200'
              )}
            >
              <p>{label}</p>
            </div>
          )}
        </div>

        <div
          id={collapseId}
          className={clstx('hidden w-full', expanded && 'block')}
          role="group"
          aria-labelledby={headingId}
        >
          <div className="ms-4 ps-4 relative before:absolute before:top-0 before:start-0 before:w-0.5 before:-ms-px before:h-full before:bg-gray-100">
            {hasChildren &&
              children?.map((child, idx) => (
                <TreeNode
                  key={child.id ?? `${nodeId}-child-${idx}`}
                  item={child}
                  path={[...path, idx]}
                  expandedIds={expandedIds}
                  onExpandItem={onExpandItem}
                  labelRenderer={labelRenderer}
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export type TreeViewItem = {
  id?: string;
  label: string;
  selected?: boolean;
  defaultOpen?: boolean;
  children?: TreeViewItem[];
  metaData?: Record<string, any>;
};

export type TreeViewItemWithId = Omit<TreeViewItem, 'id' | 'children'> & {
  id: string;
  children?: TreeViewItemWithId[];
};

type ItemRenderer = (item: TreeViewItem, expanded: boolean) => ReactNode;
type OnExpandItem = (item: TreeViewItemWithId, expanded: boolean) => void;

export type TProps = {
  className?: string;
  items: TreeViewItem[];
  expandedIds?: Set<string>;
  onExpandItem?: OnExpandItem;
  labelRenderer?: ItemRenderer;
};

const TreeView: React.FC<TProps> = (props) => {
  const { className, items, expandedIds, onExpandItem, labelRenderer } = props;

  return (
    <div
      className={clstx('typography:prose', className)}
      role="tree"
      aria-orientation="vertical"
    >
      <div role="group" className="space-y-2">
        {items.map((item, idx) => (
          <TreeNode
            key={item.id ?? idx}
            item={item}
            path={[idx]}
            expandedIds={expandedIds}
            onExpandItem={onExpandItem}
            labelRenderer={labelRenderer}
          />
        ))}
      </div>
    </div>
  );
};

export default TreeView;
